import dayjs from "dayjs";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button, { CreateButton } from "../../components/Core/Button/Button";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import Dropdown from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Pagination from "../../components/Shared/Pagination/Pagination";
import BulkRecruitmentActionModal from "../../components/Shared/Recruitment/BulkRecruitmentActionModal";
import BulkSendMessageModal from "../../components/Shared/Recruitment/BulkSendMessageModal";
import SortIcon from "../../components/Shared/SortIcon/SortIcon";
import Tab from "../../components/Shared/Tabs/Tab";
import { RecruitmentBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import { RecruitmentActionType } from "../../configs/constants/RecruitmentActionType";
import { RecruitmentStatusType } from "../../configs/constants/RecruitmentStatusType";
import { routes } from "../../configs/constants/routes";
import { NO_FILTERS_KEY_VALUE } from "../../configs/defaultFilter.config";
import { RECRUITMENT_ACTION_KEY_VALUES } from "../../configs/recruitmentActions.configs";
import useDebounce from "../../hooks/useDebounce";
import useQueryState from "../../hooks/useQueryState";
import { FilterList } from "../../models/Filters";
import { KeyValue } from "../../models/KeyValue";
import { RECRUITMENT_SORTING_COLUMN, SORT_ORDER } from "../../models/Sorting";
import { useCandidates } from "../../networking/candidate.hooks";
import { getSignedContractStatus } from "../../utils/recruitment.utils";
import { formatRoute } from "../../utils/route.utils";
import { useCompetence } from "../../networking/organisation.hooks";

const tabFilters: FilterList<RecruitmentStatusType>[] = [
	{
		name: "IN_REVIEW",
		filter: RecruitmentStatusType.IN_REVIEW,
		query: {
			status: RecruitmentStatusType.IN_REVIEW,
		},
	},
	{
		name: "HIRED",
		filter: RecruitmentStatusType.HIRED,
		query: {
			status: RecruitmentStatusType.HIRED,
		},
	},
	{
		name: "CANCELLED",
		filter: RecruitmentStatusType.CANCELLED,
		query: {
			status: RecruitmentStatusType.CANCELLED,
		},
	},
];

const RecruitmentListPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([]);
	const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
	const [showBulkActionModal, setShowBulkActionModal] = useState(false);

	const [pageNumber, setPageNumber] = useQueryState<number>("page", {
		initialValue: 1,
		valueFromString: (str) => Number(str),
	});
	const [searchTerm, setSearchTerm] = useQueryState<string>("");
	const keyword = useDebounce(searchTerm, 500);
	const [order, setOrder] = useQueryState<SORT_ORDER>("order");
	const [sort, setSort] = useQueryState<RECRUITMENT_SORTING_COLUMN>("sort");
	const [status, setStatus] = useQueryState<RecruitmentStatusType>("status", {
		initialValue: RecruitmentStatusType.IN_REVIEW,
		onChange: () => {
			setPageNumber(1);
		},
	});
	const [currentAction, setCurrentAction] = useState<RecruitmentActionType>();
	const [pool, setPool] = useQueryState<string>("pool", { initialValue: "" });
	const [competenceServices, setCompetenceServices] = useState<any>([]);
	const [competenceSkills, setCompetenceSkills] = useState<any>([]);
	const [selectedBranch, setSelectedBranch] = useState<any>(undefined);
	const [selectedService, setSelectedService] = useState<any>(undefined);
	const [skills, setSkills] = useState<any>(undefined);

	const {
		candidates,
		totalCount = 0,
		isLoading,
	} = useCandidates({
		page: pageNumber,
		pageSize: PAGE_SIZE,
		keyword,
		status: [status],
		currentAction,
		sort,
		order,
		pool,
	});

	const { data: competenceList, isLoading: competenceLoading } = useCompetence(true);

	function generateKeyName(competence: any[]) {
		if (Array.isArray(competence)) {
			const result = competence?.map((comp: any) => {
				const temp = {
					key: comp.key,
					value: comp?.name?.sv,
				};
				return temp;
			});

			return result;
		} else {
			return [];
		}
	}

	function getServiceKeyName(selectedKey: any) {
		if (selectedKey === null) {
			setSelectedBranch(undefined);
			setSelectedService(undefined);
			setCompetenceServices([]);
			setCompetenceSkills([]);
			setSkills(undefined);
			return;
		}
		if (Array.isArray(competenceList?.competences)) {
			const selectedBranchTemp = competenceList?.competences?.find(
				(comp: any) => comp.key === selectedKey
			);
			if (Array.isArray(selectedBranchTemp?.items)) {
				const result = selectedBranchTemp?.items?.map((comp: any) => {
					const temp = {
						key: comp.key,
						value: comp?.name?.sv,
						data: comp?.items,
					};
					return temp;
				});
				setSelectedBranch(selectedBranchTemp);

				setCompetenceServices(result);
			} else {
				setCompetenceServices([]);
			}
		} else {
			setCompetenceServices([]);
		}
	}

	function getSkillKeyName(selectedKey: any) {
		if (selectedKey === null) {
			setSelectedService(undefined);
			setCompetenceSkills([]);
			setSkills(undefined);
			return;
		}

		if (Array.isArray(competenceServices)) {
			const selectedServiceTemp = competenceServices?.find((comp: any) => comp.key === selectedKey);
			const result = selectedServiceTemp?.data?.map((comp: any) => {
				const temp = {
					key: comp.key,
					value: comp?.name.sv,
				};
				return temp;
			});

			setCompetenceSkills(result);
			setSelectedService(selectedServiceTemp);
		} else {
			setCompetenceSkills([]);
			setSelectedService(undefined);
		}
	}

	function setSkillsInfo(selectedKey: any) {
		setSkills(selectedKey);
	}

	const navigateToRecruitmentDetails = (id?: string) => {
		const routePath = formatRoute(
			[routes.RECRUITMENT_DETAILS, routes.RECRUITMENT_PROFILE].join("/"),
			[id]
		);
		navigate(routePath);
	};

	const handleSortColumn = async (sort?: RECRUITMENT_SORTING_COLUMN) => {
		handleSortingOrder();
		setSort(sort);
	};

	const handleSortingOrder = () => {
		if (!order) {
			setOrder(SORT_ORDER.ASC);
		} else if (order === SORT_ORDER.ASC) {
			setOrder(SORT_ORDER.DESC);
		} else if (order === SORT_ORDER.DESC) {
			setOrder(SORT_ORDER.ASC);
		}
	};

	const handleCurrentActionFilter = (currentAction: RecruitmentActionType) => {
		if (currentAction === NO_FILTERS_KEY_VALUE[0].key) setCurrentAction(undefined);
		else setCurrentAction(currentAction);
	};

	const filterByCompetence = useCallback(() => {
		if (Array.isArray(candidates)) {
			const selectedSkills = [...[skills || ""]];

			const filtered = candidates.filter((item) => {
				if (!selectedBranch) {
					return true;
				}

				return item.profile.competences?.some((comp) => {
					const branchMatch = !selectedBranch || comp.branch === selectedBranch?.key;
					const serviceMatch = !selectedService || comp.service === selectedService?.key;

					const skillsMatch =
						!selectedSkills ||
						selectedSkills.length === 0 ||
						(Array.isArray(comp.skills) &&
							selectedSkills.some((skill) => comp.skills?.includes(skill) ?? false));
					if (selectedBranch && !selectedService && !skills) {
						// console.log("selectedService 0", selectedService);
						return branchMatch;
					} else if (selectedBranch && selectedService && !skills) {
						// console.log("selectedService", selectedService);
						return branchMatch && serviceMatch;
					} else if (selectedBranch && selectedService && skills) {
						return branchMatch && serviceMatch && skillsMatch;
					}
					return true;
				});
			});
			return filtered;
		} else {
			return [];
		}
	}, [selectedBranch, skills, selectedService, candidates]);

	const visibleCandidates = filterByCompetence();

	const allVisibleSelected =
		visibleCandidates.length > 0 &&
		visibleCandidates.every((c) => c.id && selectedCandidateIds.includes(c.id));

	const handleToggleSelectAll = () => {
		if (allVisibleSelected) {
			const visibleIds = new Set(visibleCandidates.map((c) => c.id));
			setSelectedCandidateIds((prev) => prev.filter((id) => !visibleIds.has(id)));
		} else {
			const visibleIds = visibleCandidates.map((c) => c.id).filter(Boolean) as string[];
			setSelectedCandidateIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
		}
	};

	const handleToggleCandidate = (e: React.MouseEvent, id: string) => {
		e.stopPropagation();
		setSelectedCandidateIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	const selectedNames = visibleCandidates
		.filter((c) => c.id && selectedCandidateIds.includes(c.id))
		.map((c) => `${c.firstName} ${c.lastName}`);

	return (
		<>
			<Breadcrumbs
				items={RecruitmentBreadcrumbs}
				onChangePool={setPool}
				selectedPool={pool}
				showPoolDropdown
			/>
			{showBulkMessageModal && (
				<BulkSendMessageModal
					hideModal={() => setShowBulkMessageModal(false)}
					userIds={selectedCandidateIds}
					recipientNames={selectedNames}
				/>
			)}
			{showBulkActionModal && (
				<BulkRecruitmentActionModal
					hideModal={() => setShowBulkActionModal(false)}
					userIds={selectedCandidateIds}
				/>
			)}

			<div className="p-4 px-6">
				<Flex justifyContent="space-between">
					<AppInput
						margin="0.5rem 0 0 0"
						containerWidth="200px"
						placeholder={t("SEARCH")}
						icon="search"
						onChange={(e: any) => setSearchTerm(e.target.value)}
					/>
					<Link to={routes.RECRUITMENT_CREATE}>
						<CreateButton margin="0 1rem 1rem">{t("CREATE_CANDIDATE")}</CreateButton>
					</Link>
				</Flex>

				<Flex margin="0 0 0.75rem 0" alignItems="flex-end">
					<Flex gap="0.6rem">
						{tabFilters.map((filterTab, index) => (
							<Tab
								action={() => setStatus(filterTab.filter)}
								key={index}
								active={filterTab.filter === status}
								text={filterTab.name}
								role="filter"
							/>
						))}
					</Flex>
					<Flex justifyContent="flex-end" alignItems="flex-end">
						<Dropdown<KeyValue, any>
							onChange={getServiceKeyName}
							getValue={(o) => o.key as RecruitmentActionType}
							getOptionLabel={(option) => t(option.value as any)}
							options={generateKeyName(competenceList?.competences || [])}
							placeholder={t("INDUSTRY")}
							label={t("INDUSTRY")}
							margin="0 1rem 0 0"
							width="200px"
						/>
						<Dropdown<KeyValue, any>
							onChange={getSkillKeyName}
							getValue={(o) => o.key as RecruitmentActionType}
							getOptionLabel={(option) => t(option.value as any)}
							options={competenceServices}
							placeholder={t("SERVICE")}
							label={t("SERVICE")}
							margin="0 1rem 0 0"
							width="200px"
						/>
						<Dropdown<KeyValue, any>
							onChange={setSkillsInfo}
							getValue={(o) => o.key as RecruitmentActionType}
							getOptionLabel={(option) => t(option.value as any)}
							options={competenceSkills}
							placeholder={t("COMPETENCE")}
							label={t("COMPETENCE")}
							margin="0 1rem 0 0"
							width="200px"
						/>
						<Dropdown<KeyValue, RecruitmentActionType>
							onChange={handleCurrentActionFilter}
							getValue={(o) => o.key as RecruitmentActionType}
							value={{
								key: currentAction ? currentAction : "",
								value: currentAction ? currentAction : t("ALL"),
							}}
							getOptionLabel={(option) => t(option.value as any)}
							options={NO_FILTERS_KEY_VALUE.concat(RECRUITMENT_ACTION_KEY_VALUES)}
							placeholder={t("CURRENT_ACTION")}
							label={t("CURRENT_ACTION")}
							margin="0 1rem 0 0"
							width="200px"
						/>
					</Flex>
				</Flex>

				{selectedCandidateIds.length > 0 && (
					<Flex
						alignItems="center"
						gap="1rem"
						padding="0.625rem 1rem"
						backgroundColor="white"
						borderRadius="6px"
						border="1px solid #DBDBDB"
						width="auto"
						style={{
							position: "fixed",
							bottom: "1.5rem",
							left: "50%",
							transform: "translateX(-50%)",
							zIndex: 10,
							boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
						}}
					>
						<AppText size="s" weight="bold">
							{selectedCandidateIds.length} {t("recruitment.bulk.selected")}
						</AppText>
						<Button
							fontSize="s"
							width="auto"
							margin="0"
							backgroundColor="white"
							textColor="grayTextDark"
							border="1px solid #DBDBDB"
							onClick={() => setShowBulkMessageModal(true)}
						>
							{t("recruitment.bulk.sendMessage")}
						</Button>
						<Button
							fontSize="s"
							width="auto"
							margin="0"
							backgroundColor="white"
							textColor="grayTextDark"
							border="1px solid #DBDBDB"
							onClick={() => setShowBulkActionModal(true)}
						>
							{t("recruitment.bulk.updateAction")}
						</Button>
						<Button
							fontSize="s"
							width="auto"
							margin="0"
							backgroundColor="white"
							textColor="grayTextDark"
							border="1px solid #DBDBDB"
							onClick={() => setSelectedCandidateIds([])}
						>
							{t("recruitment.bulk.clearSelection")}
						</Button>
					</Flex>
				)}

				{isLoading ? (
					<LoadingIndicator />
				) : (
					<div>
						{candidates?.length ? (
							<TableLayout>
								<table>
									<thead>
										<tr>
											<th style={{ width: 40 }} onClick={(e) => e.stopPropagation()}>
												<Checkbox isChecked={allVisibleSelected} onChange={handleToggleSelectAll} />
											</th>
											<th
												onClick={() => handleSortColumn(RECRUITMENT_SORTING_COLUMN.FIRST_NAME)}
												style={{ width: 250 }}
											>
												{t("FIRST_NAME")}
												<SortIcon
													sortType={
														sort === RECRUITMENT_SORTING_COLUMN.FIRST_NAME
															? order
															: SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th
												onClick={() => handleSortColumn(RECRUITMENT_SORTING_COLUMN.LAST_NAME)}
												style={{ width: 250 }}
											>
												{t("LAST_NAME")}
												<SortIcon
													sortType={
														sort === RECRUITMENT_SORTING_COLUMN.LAST_NAME
															? order
															: SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th>{t("CITY")}</th>
											<th
												onClick={() => handleSortColumn(RECRUITMENT_SORTING_COLUMN.CREATED)}
												style={{ width: 150 }}
											>
												{t("RECEIVED")}

												<SortIcon
													sortType={
														sort === RECRUITMENT_SORTING_COLUMN.CREATED ? order : SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th>{t("CURRENT_ACTION")}</th>
											<th
												onClick={() =>
													handleSortColumn(RECRUITMENT_SORTING_COLUMN.NEXT_ACTION_DATE)
												}
											>
												{t("NEXT_ACTION_DATE")}
												<SortIcon
													sortType={
														sort === RECRUITMENT_SORTING_COLUMN.NEXT_ACTION_DATE
															? order
															: SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th onClick={() => handleSortColumn(RECRUITMENT_SORTING_COLUMN.UPDATED)}>
												{t("LAST_EDITED")}
												<SortIcon
													sortType={
														sort === RECRUITMENT_SORTING_COLUMN.UPDATED ? order : SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th onClick={() => handleSortColumn(RECRUITMENT_SORTING_COLUMN.AD)}>
												{t("recruitment.adId")}
												<SortIcon
													sortType={
														sort === RECRUITMENT_SORTING_COLUMN.AD ? order : SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th>{t("CONTRACT")}</th>
											<th>{t("recruitment.otherNotes")}</th>
										</tr>
									</thead>
									<tbody>
										{visibleCandidates?.map(({ id, firstName, lastName, metadata, profile }) => {
											const isSelected = !!id && selectedCandidateIds.includes(id);
											return (
												<tr key={id} onClick={() => navigateToRecruitmentDetails(id)}>
													<td onClick={(e) => id && handleToggleCandidate(e, id)}>
														<Checkbox isChecked={isSelected} onChange={() => {}} />
													</td>
													<td>{firstName}</td>
													<td>{lastName}</td>
													<td>{profile?.address?.city || "-"}</td>
													<td>{dayjs(metadata?.created).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
													<td>{t((profile?.recruitment?.currentAction || "") as any)}</td>
													<td>
														{profile?.recruitment?.nextActionTime ? (
															<AppText>
																{dayjs(profile?.recruitment.nextActionTime).format(
																	DATE_TIME_FORMAT_TYPES.DATE_TIME
																)}
															</AppText>
														) : (
															""
														)}
													</td>
													<td>
														{dayjs(metadata?.updated).format(DATE_TIME_FORMAT_TYPES.DATE)}
														{metadata?.updatedBy && `${t("BY")} ${metadata.updatedBy}`}
													</td>
													<td>{profile.recruitment?.adId || "-"}</td>
													<td>
														{getSignedContractStatus(profile?.employment?.employmentContract, t)}
													</td>
													<td>
														<AppText size="s" italic lineClamp={1}>
															{profile.internalNotes || "-"}
														</AppText>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</TableLayout>
						) : (
							<EmptyState margin="2rem 0" label="NO_CANDIDATES" />
						)}
						{totalCount > PAGE_SIZE && (
							<Pagination
								currentPage={pageNumber}
								totalCount={totalCount}
								onClickItem={(e) => setPageNumber(e)}
							/>
						)}
					</div>
				)}
			</div>
		</>
	);
};

export default RecruitmentListPage;
